import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { formatFileSize } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button } from '../../../ds/components';
import { ApiError, GENERIC_ERROR_MESSAGE } from '../../../lib/api';
import { UploadInterruptedError, uploadOfferWithProgress, type OfferUploadRow } from './api';
import styles from './financier.module.css';

/**
 * Offer uploads (Finanzierer 06/10/11): the dropzone, the client-side upload queue with progress (sequential, one
 * request per file) and the rows of „Hochgeladene Dateien" — server drafts (`uploads`) and client states (running,
 * connection lost „bei 62 %", not a PDF).
 */

export const WRONG_TYPE_MESSAGE =
  'Nur PDF möglich. Speichern Sie das Dokument als PDF und laden Sie es erneut hoch.';
const PDF_ACCEPT = 'application/pdf,.pdf';

export function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

// --- a header button („Angebot hochladen") asks the mounted offers tab to open its file picker -------------------------

type PickHandler = (replacesOfferId: string | null) => void;
const pickHandlers = new Map<string, PickHandler>();

/** Opens the offer file picker of the mounted offers tab of this financing; false when none is mounted. */
export function requestOfferUpload(financingId: string, replacesOfferId: string | null = null): boolean {
  const handler = pickHandlers.get(financingId);
  if (!handler) return false;
  handler(replacesOfferId);
  return true;
}

function useOfferPickRegistration(financingId: string, handler: PickHandler): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const stable: PickHandler = (replaces) => ref.current(replaces);
    pickHandlers.set(financingId, stable);
    return () => {
      if (pickHandlers.get(financingId) === stable) pickHandlers.delete(financingId);
    };
  }, [financingId]);
}

// --- client queue ----------------------------------------------------------------------------------------------------------

export interface ClientUpload {
  id: string;
  file: File;
  replacesOfferId: string | null;
  status: 'queued' | 'uploading' | 'interrupted' | 'rejected';
  percent: number;
  message: string | null;
}

let nextId = 1;

export function useOfferUploadQueue(financingId: string, onUploaded: () => void) {
  const [entries, setEntries] = useState<ClientUpload[]>([]);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const uploadedRef = useRef(onUploaded);
  uploadedRef.current = onUploaded;

  const patch = useCallback((id: string, change: Partial<ClientUpload>) => {
    setEntries((current) => current.map((entry) => (entry.id === id ? { ...entry, ...change } : entry)));
  }, []);

  const run = useCallback(
    (entry: ClientUpload) => {
      chain.current = chain.current.then(async () => {
        patch(entry.id, { status: 'uploading', percent: 0, message: null });
        try {
          await uploadOfferWithProgress(financingId, entry.file, {
            replacesOfferId: entry.replacesOfferId,
            onProgress: (percent) => patch(entry.id, { percent }),
          });
          setEntries((current) => current.filter((item) => item.id !== entry.id));
          uploadedRef.current();
        } catch (error) {
          if (error instanceof UploadInterruptedError) {
            patch(entry.id, { status: 'interrupted', percent: error.percent });
          } else {
            patch(entry.id, {
              status: 'rejected',
              message: error instanceof ApiError ? error.message : GENERIC_ERROR_MESSAGE,
            });
          }
        }
      });
    },
    [financingId, patch],
  );

  const add = useCallback(
    (files: File[], replacesOfferId: string | null) => {
      const created = files.map<ClientUpload>((file) => ({
        id: `upload-${nextId++}`,
        file,
        replacesOfferId,
        status: isPdf(file) ? 'queued' : 'rejected',
        percent: 0,
        message: isPdf(file) ? null : WRONG_TYPE_MESSAGE,
      }));
      setEntries((current) => [...current, ...created]);
      for (const entry of created) if (entry.status === 'queued') run(entry);
    },
    [run],
  );

  const retry = useCallback(
    (id: string) => {
      const entry = entries.find((item) => item.id === id);
      if (entry) run(entry);
    },
    [entries, run],
  );

  const remove = useCallback((id: string) => {
    setEntries((current) => current.filter((item) => item.id !== id));
  }, []);

  return { entries, add, retry, remove };
}

/** A hidden file input + picker opened by the dropzone, the row buttons and the header („Angebot hochladen"). */
export function useOfferPicker(
  financingId: string,
  onFiles: (files: File[], replacesOfferId: string | null) => void,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const replacesRef = useRef<string | null>(null);
  const open = useCallback((replacesOfferId: string | null = null) => {
    replacesRef.current = replacesOfferId;
    inputRef.current?.click();
  }, []);
  useOfferPickRegistration(financingId, open);
  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={PDF_ACCEPT}
      multiple
      className={styles.srOnly}
      tabIndex={-1}
      aria-label="Angebot hochladen"
      onChange={(event) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length > 0) onFiles(replacesRef.current ? files.slice(0, 1) : files, replacesRef.current);
        replacesRef.current = null;
      }}
    />
  );
  return { open, input };
}

/** „Weiteres Angebot hochladen" — PDF hierher ziehen oder auswählen · mehrere Dateien möglich. */
export function OfferDropzone({ onPick, onFiles }: { onPick: () => void; onFiles: (files: File[]) => void }) {
  const [over, setOver] = useState(false);
  function drop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setOver(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) onFiles(files);
  }
  return (
    <button
      type="button"
      className={clsx(styles.dropzone, over && styles.dropzoneOver)}
      onClick={onPick}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <span className={styles.dropTitle}>Weiteres Angebot hochladen</span>
      <span className={styles.dropText}>PDF hierher ziehen oder auswählen · mehrere Dateien möglich</span>
    </button>
  );
}

function serverTone(state: OfferUploadRow['state']): 'danger' | 'neutral' {
  return state === 'unreadable' || state === 'failed' ? 'danger' : 'neutral';
}

/** „Hochgeladene Dateien" (Finanzierer 10): server drafts first, then the client states. */
export function UploadRows({
  uploads,
  entries,
  canEdit,
  busyOfferId,
  onManual,
  onOtherFile,
  onRetry,
  onRemove,
}: {
  uploads: OfferUploadRow[];
  entries: ClientUpload[];
  canEdit: boolean;
  busyOfferId: string | null;
  onManual: (upload: OfferUploadRow) => void;
  onOtherFile: (upload: OfferUploadRow) => void;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className={styles.uploads}>
      {uploads.map((upload) => {
        const tone = serverTone(upload.state);
        const busy = busyOfferId === upload.offerId;
        return (
          <div
            key={upload.offerId}
            className={clsx(styles.uploadRow, tone === 'danger' && styles.uploadDanger)}
          >
            <span className={styles.uploadText}>
              <span className={styles.uploadName}>
                {upload.fileName}
                {upload.sizeLabel ? ` · ${upload.sizeLabel}` : ''}
              </span>
              <span className={styles.uploadMessage}>{upload.message}</span>
            </span>
            {canEdit && upload.state !== 'pending' ? (
              <>
                <Button variant="secondary" size="small" disabled={busy} onClick={() => onOtherFile(upload)}>
                  Andere Datei
                </Button>
                <Button className={styles.rowButton} disabled={busy} onClick={() => onManual(upload)}>
                  Selbst eintragen
                </Button>
              </>
            ) : null}
          </div>
        );
      })}
      {entries.map((entry) => {
        const name = `${entry.file.name}${entry.status === 'interrupted' ? ` · ${formatFileSize(entry.file.size)}` : ''}`;
        if (entry.status === 'queued' || entry.status === 'uploading') {
          return (
            <div key={entry.id} className={styles.uploadRow} aria-busy="true">
              <span className={styles.uploadText}>
                <span className={styles.uploadName}>{entry.file.name}</span>
                <span className={styles.uploadMessage}>
                  {entry.status === 'queued'
                    ? 'Wartet auf den Upload.'
                    : `Wird hochgeladen · ${entry.percent} %`}
                </span>
                <span
                  className={styles.uploadProgress}
                  role="progressbar"
                  aria-label={entry.file.name}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={entry.percent}
                >
                  <span
                    className={styles.uploadProgressFill}
                    style={{ width: `${entry.percent}%`, display: 'block' }}
                  />
                </span>
              </span>
            </div>
          );
        }
        if (entry.status === 'interrupted') {
          return (
            <div key={entry.id} className={clsx(styles.uploadRow, styles.uploadWarning)} role="alert">
              <span className={styles.uploadText}>
                <span className={styles.uploadName}>{name}</span>
                <span className={styles.uploadMessage}>
                  {`Die Datei konnte nicht hochgeladen werden. Die Verbindung wurde unterbrochen bei ${entry.percent} %.`}
                </span>
              </span>
              <Button variant="secondary" size="small" onClick={() => onRemove(entry.id)}>
                Entfernen
              </Button>
              <Button className={styles.rowButton} onClick={() => onRetry(entry.id)}>
                Erneut versuchen
              </Button>
            </div>
          );
        }
        return (
          <div key={entry.id} className={styles.uploadRow} role="alert">
            <span className={styles.uploadText}>
              <span className={styles.uploadName}>{entry.file.name}</span>
              <span className={styles.uploadMessage}>{entry.message}</span>
            </span>
            <Button variant="secondary" size="small" onClick={() => onRemove(entry.id)}>
              Entfernen
            </Button>
          </div>
        );
      })}
    </div>
  );
}
